import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../hooks/usePageSEO';
import { 
  Apple, 
  TrendingUp, 
  Restaurant, 
  FitnessCenter, 
  WaterDrop, 
  Flag, 
  Favorite, 
  LocalPharmacy,
  CalendarMonth,
  Notifications,
  Star,
  Warning,
  Schedule,
  Security,
  Verified,
  Shield,
  Lock,
  CheckCircle
} from '../mui-icons';
import { ReactComponent as AIIcon } from '../assets/icons/ai.svg';
import iconImage from '../assets/icon.webp';
import viniPhoto from '../assets/profiles/vini.jpg';
// Import carousel images (WebP for better performance)
import appImage1 from '../assets/app/1.webp';
import appImage2 from '../assets/app/2.webp';
import appImage4 from '../assets/app/4.webp';
import appImage6 from '../assets/app/6.webp';
import appImage7 from '../assets/app/7.webp';
import './IndexPage.css';

const IndexPage = () => {
  const { t, translateHTML } = useTranslation();
  
  // SEO dinâmico para esta página
  usePageSEO();
  
  // Carousel images array
  const carouselImages = [appImage1, appImage2, appImage4, appImage6, appImage7];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCookieBanner, setShowCookieBanner] = useState(false);


  // Effect to change image every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // Check if it's the first visit to show cookie banner
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowCookieBanner(true);
    }
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowCookieBanner(false);
  };

  const handleCookieLearnMore = () => {
    // Redirect to privacy policy page
    window.open('/policy', '_blank');
  };

  // Protection against extension errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.error && event.error.message && 
          event.error.message.includes('onMessage')) {
        console.warn('Extension error detected and ignored:', event.error);
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <div className="index-page surface-marketing">
      <Header />

      <main id="main-content" role="main">
        {/* Animated Particles Background */}
        <div className="particles-container" aria-hidden="true">
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
          <div className="particle particle-5"></div>
          <div className="particle particle-6"></div>
          <div className="particle particle-7"></div>
          <div className="particle particle-8"></div>
        </div>

        {/* Hero Section */}
        <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-content">
            <h1 className="hero-title" dangerouslySetInnerHTML={translateHTML('home.hero.title')}>
            </h1>
            <p className="hero-subtitle">
              {t('home.hero.subtitle')}
            </p>
            <div className="hero-buttons">
              <div className="store-buttons">
                <a href="https://apps.apple.com/us/app/personalnutri/id6753727736" target="_blank" rel="noopener noreferrer" className="store-button apple-store">
                  <div className="badge-content">
                    <Apple sx={{ fontSize: 20 }} />
                    <div className="badge-text">
                      <span className="badge-small">Download on the</span>
                      <span className="badge-large">App Store</span>
                    </div>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.personalnutri.app&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="store-button google-play">
                  <div className="badge-content">
                    <div className="play-icon">▶</div>
                    <div className="badge-text">
                      <span className="badge-small">GET IT ON</span>
                      <span className="badge-large">Google Play</span>
                    </div>
                  </div>
                </a>
              </div>
              <a href="/about" className="btn btn-secondary">
                {t('common.learnMore')}
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="carousel-container">
              <div className="carousel-track">
                {carouselImages.map((image, index) => (
                  <div 
                    key={index} 
                    className={`carousel-slide ${index === currentImageIndex ? 'active' : ''}`}
                  >
                    <img 
                      src={image} 
                      alt={`PersonalNutri App Screenshot ${index + 1}`} 
                      className="carousel-image"
                      width={300}
                      height={649}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={index === 0 ? "high" : "auto"}
                    />
                  </div>
                ))}
              </div>
              
              {/* Indicadores do carrossel */}
              <div className="carousel-indicators" role="tablist" aria-label="Navegação do carrossel de imagens">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    role="tab"
                    aria-selected={index === currentImageIndex}
                    aria-label={`Ir para imagem ${index + 1} de ${carouselImages.length}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">{t('home.features.title')}</h2>
          <p className="section-subtitle">
            {t('home.features.subtitle')}
          </p>

          <div className="features-grid">
            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #4CAF50, #8BC34A)' }}>
                <Apple sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.nutrition.title')}</h3>
              <p className="feature-description">
                {t('home.features.nutrition.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.nutrition.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon ai-icon" style={{ background: 'linear-gradient(135deg, #2196F3, #21CBF3)' }}>
                <AIIcon style={{ width: 40, height: 40, fill: 'white' }} />
              </div>
              <h3 className="feature-title">{t('home.features.ai.title')}</h3>
              <p className="feature-description">
                {t('home.features.ai.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.ai.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #FF9800, #FFB74D)' }}>
                <TrendingUp sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.progress.title')}</h3>
              <p className="feature-description">
                {t('home.features.progress.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.progress.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #00BCD4, #00E5FF)' }}>
                <WaterDrop sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.water.title')}</h3>
              <p className="feature-description">
                {t('home.features.water.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.water.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #9C27B0, #BA68C8)' }}>
                <Flag sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.goals.title')}</h3>
              <p className="feature-description">
                {t('home.features.goals.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.goals.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #F44336, #EF5350)' }}>
                <FitnessCenter sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.activities.title')}</h3>
              <p className="feature-description">
                {t('home.features.activities.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.activities.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #E91E63, #F06292)' }}>
                <Favorite sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.cycle.title')}</h3>
              <p className="feature-description">
                {t('home.features.cycle.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.cycle.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #3F51B5, #5C6BC0)' }}>
                <LocalPharmacy sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.medication.title')}</h3>
              <p className="feature-description">
                {t('home.features.medication.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.medication.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #FF5722, #FF7043)' }}>
                <Warning sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.allergies.title')}</h3>
              <p className="feature-description">
                {t('home.features.allergies.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.allergies.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #607D8B, #90A4AE)' }}>
                <Schedule sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.routines.title')}</h3>
              <p className="feature-description">
                {t('home.features.routines.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.routines.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card variant="marketing" className="feature-card">
              <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #795548, #A1887F)' }}>
                <Notifications sx={{ fontSize: 40 }} />
              </div>
              <h3 className="feature-title">{t('home.features.notifications.title')}</h3>
              <p className="feature-description">
                {t('home.features.notifications.description')}
              </p>
              <ul className="feature-list">
                {t('home.features.notifications.items').map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <Restaurant sx={{ fontSize: 48, color: 'var(--primary-green)' }} />
              <h3 className="stat-number">10,000+</h3>
              <p className="stat-label">{t('home.stats.meals')}</p>
            </div>
            <div className="stat-item">
              <FitnessCenter sx={{ fontSize: 48, color: 'var(--primary-green)' }} />
              <h3 className="stat-number">5,000+</h3>
              <p className="stat-label">{t('home.stats.users')}</p>
            </div>
            <div className="stat-item">
              <TrendingUp sx={{ fontSize: 48, color: 'var(--primary-green)' }} />
              <h3 className="stat-number">+95%</h3>
              <p className="stat-label">{t('home.stats.satisfaction')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="trust-badges-section">
        <div className="container">
          <h2 className="section-title">{t('home.trust.title')}</h2>
          <p className="section-subtitle">
            {t('home.trust.subtitle')}
          </p>
          
          <div className="trust-badges-grid">
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <Security sx={{ fontSize: 32, color: 'var(--primary-blue)' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.lgpd.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.lgpd.description')}</p>
              </div>
            </div>
            
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <Shield sx={{ fontSize: 32, color: 'var(--primary-green)' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.ssl.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.ssl.description')}</p>
              </div>
            </div>
            
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <Verified sx={{ fontSize: 32, color: 'var(--accent-orange)' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.medical.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.medical.description')}</p>
              </div>
            </div>
            
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <Lock sx={{ fontSize: 32, color: 'var(--accent-purple)' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.private.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.private.description')}</p>
              </div>
            </div>
            
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <CheckCircle sx={{ fontSize: 32, color: 'var(--primary-green)' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.natural.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.natural.description')}</p>
              </div>
            </div>
            
            <div className="trust-badge">
              <div className="trust-badge-icon">
                <Star sx={{ fontSize: 32, color: '#FFB800' }} />
              </div>
              <div className="trust-badge-content">
                <h3 className="trust-badge-title">{t('home.trust.rating.title')}</h3>
                <p className="trust-badge-description">{t('home.trust.rating.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-section">
        <div className="container">
          <h2 className="section-title">{t('home.ratings.title')}</h2>
          <p className="section-subtitle">
            {t('home.ratings.subtitle')}
          </p>

          <div className="reviews-layout">
            
            <div className="rating-card">
              <div className="rating-header">
                <div className="rating-stars">
                  <Star className="star-icon" />
                  <Star className="star-icon" />
                  <Star className="star-icon" />
                  <Star className="star-icon" />
                  <Star className="star-icon" />
                </div>
                <div className="rating-score">4.8/5</div>
              </div>
              <div className="rating-details">
                <div className="rating-count">{t('home.ratings.reviews')}</div>
                <div className="rating-platform">Google Play Store</div>
              </div>
              <div className="download-buttons">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.personalnutri.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-btn google-play"
                >
                  <div className="btn-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path fill="currentColor" d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                  </div>
                  <div className="btn-content">
                    <div className="btn-text">{t('home.ratings.download')}</div>
                    <div className="btn-store">Google Play</div>
                  </div>
                </a>
                
                <a 
                  href="https://apps.apple.com/us/app/personalnutri/id6753727736"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-btn apple-store"
                >
                  <div className="btn-icon">
                    <Apple />
                  </div>
                  <div className="btn-content">
                    <div className="btn-text">{t('home.ratings.apple')}</div>
                    <div className="btn-store">App Store</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="review-card">
              <div className="review-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} sx={{ fontSize: 24, color: '#FFB800' }} />
                ))}
              </div>
              <p className="review-text">
                "O PersonalNutri mudou completamente minha vida! A IA me ajudou a entender melhor minha alimentação e criar hábitos realmente saudáveis. Em 3 meses, já vi resultados incríveis não só no físico, mas principalmente na disposição e energia do dia a dia. O app é super completo, intuitivo e realmente funciona. Recomendo demais!"
              </p>
              <div className="review-author">
                <div className="author-avatar">
                  <img 
                    src={viniPhoto} 
                    alt="Vinicius Meggiotto" 
                    className="author-photo"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="author-info">
                  <h4 className="author-name">Vinicius Meggiotto</h4>
                  <p className="author-title">{t('home.ratings.premiumUser')}</p>
                </div>
              </div>
            </div>
            
          </div>
        </div>
        </section>


        {/* Clear Benefits Section */}
        <section className="benefits-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{t('home.benefits.title')}</h2>
              <p className="section-subtitle">
                {t('home.benefits.subtitle')}
              </p>
            </div>
            
            <div className="benefits-grid">
              <div className="benefit-card primary">
                <div className="benefit-icon">
                  <Restaurant />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.nutritional.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.nutritional.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">+85%</span>
                    <span className="benefit-label">{t('home.benefits.nutritional.highlight')}</span>
                  </div>
                </div>
              </div>
              
              <div className="benefit-card secondary">
                <div className="benefit-icon">
                  <FitnessCenter />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.tracking.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.tracking.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">3x</span>
                    <span className="benefit-label">{t('home.benefits.tracking.highlight')}</span>
                  </div>
                </div>
              </div>
              
              <div className="benefit-card accent">
                <div className="benefit-icon">
                  <TrendingUp />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.results.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.results.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">94%</span>
                    <span className="benefit-label">{t('home.benefits.results.highlight')}</span>
                  </div>
                </div>
              </div>
              
              <div className="benefit-card success">
                <div className="benefit-icon">
                  <WaterDrop />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.hydration.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.hydration.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">2.5L</span>
                    <span className="benefit-label">{t('home.benefits.hydration.highlight')}</span>
                  </div>
                </div>
              </div>
              
              <div className="benefit-card warning">
                <div className="benefit-icon">
                  <LocalPharmacy />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.supplementation.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.supplementation.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">100%</span>
                    <span className="benefit-label">{t('home.benefits.supplementation.highlight')}</span>
                  </div>
                </div>
              </div>
              
              <div className="benefit-card info">
                <div className="benefit-icon">
                  <CalendarMonth />
                </div>
                <div className="benefit-content">
                  <h3 className="benefit-title">{t('home.benefits.flexibility.title')}</h3>
                  <p className="benefit-description">
                    {t('home.benefits.flexibility.description')}
                  </p>
                  <div className="benefit-highlight">
                    <span className="benefit-value">24/7</span>
                    <span className="benefit-label">{t('home.benefits.flexibility.highlight')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Progress Chart Section */}
        <section className="progress-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{t('home.progress.title')}</h2>
              <p className="section-subtitle">
                {t('home.progress.subtitle')}
              </p>
            </div>
            
            <div className="progress-charts">
              <div className="chart-container">
                <div className="chart-header">
                  <h3 className="chart-title">{t('home.progress.weight.title')}</h3>
                  <div className="chart-period">{t('home.progress.weight.period')}</div>
                </div>
                <div className="progress-chart">
                  <div className="chart-line">
                    <div className="chart-point" style={{left: '10%', bottom: '20%'}}></div>
                    <div className="chart-point" style={{left: '25%', bottom: '35%'}}></div>
                    <div className="chart-point" style={{left: '40%', bottom: '50%'}}></div>
                    <div className="chart-point" style={{left: '55%', bottom: '65%'}}></div>
                    <div className="chart-point" style={{left: '70%', bottom: '80%'}}></div>
                    <div className="chart-point active" style={{left: '85%', bottom: '90%'}}></div>
                  </div>
                  <div className="chart-labels">
                    <span>{t('home.progress.weeks.week1')}</span>
                    <span>{t('home.progress.weeks.week2')}</span>
                    <span>{t('home.progress.weeks.week3')}</span>
                    <span>{t('home.progress.weeks.week4')}</span>
                  </div>
                </div>
                <div className="chart-stats">
                  <div className="stat-item">
                    <span className="stat-value">-3.2kg</span>
                    <span className="stat-label">{t('home.progress.weight.lost')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">85%</span>
                    <span className="stat-label">{t('home.progress.weight.goal')}</span>
                  </div>
                </div>
              </div>
              
              <div className="chart-container">
                <div className="chart-header">
                  <h3 className="chart-title">{t('home.progress.calories.title')}</h3>
                  <div className="chart-period">{t('home.progress.calories.period')}</div>
                </div>
                <div className="circular-progress">
                  <div className="progress-ring">
                    <svg className="progress-ring-svg" width="120" height="120">
                      <circle
                        className="progress-ring-circle-bg"
                        stroke="#E0E0E0"
                        strokeWidth="8"
                        fill="transparent"
                        r="52"
                        cx="60"
                        cy="60"
                      />
                      <circle
                        className="progress-ring-circle"
                        stroke="url(#gradient)"
                        strokeWidth="8"
                        fill="transparent"
                        r="52"
                        cx="60"
                        cy="60"
                        strokeDasharray="326.73"
                        strokeDashoffset="65.35"
                        style={{animation: 'progressAnimation 2s ease-in-out'}}
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#4CAF50" />
                          <stop offset="100%" stopColor="#8BC34A" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="progress-text">
                      <span className="progress-value">1,850</span>
                      <span className="progress-label">kcal</span>
                    </div>
                  </div>
                </div>
                <div className="chart-stats">
                  <div className="stat-item">
                    <span className="stat-value">80%</span>
                    <span className="stat-label">{t('home.progress.calories.dailyGoal')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">+150</span>
                    <span className="stat-label">{t('home.progress.calories.remaining')}</span>
                  </div>
                </div>
              </div>
              
              <div className="chart-container">
                <div className="chart-header">
                  <h3 className="chart-title">{t('home.progress.water.title')}</h3>
                  <div className="chart-period">{t('home.progress.water.period')}</div>
                </div>
                <div className="water-progress">
                  <div className="water-bottles">
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle filled"></div>
                    <div className="water-bottle"></div>
                    <div className="water-bottle"></div>
                  </div>
                  <div className="water-level">
                    <div className="water-fill" style={{height: '75%'}}></div>
                  </div>
                </div>
                <div className="chart-stats">
                  <div className="stat-item">
                    <span className="stat-value">1.5L</span>
                    <span className="stat-label">{t('home.progress.water.consumed')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">75%</span>
                    <span className="stat-label">{t('home.progress.water.goal')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container cta-container">
          <h2 className="cta-title">{t('home.cta.title')}</h2>
          <p className="cta-subtitle">
            {t('home.cta.subtitle')}
          </p>
          <div className="store-buttons">
            <a href="https://apps.apple.com/us/app/personalnutri/id6753727736" target="_blank" rel="noopener noreferrer" className="store-button apple-store">
              <div className="badge-content">
                <Apple sx={{ fontSize: 20 }} />
                <div className="badge-text">
                  <span className="badge-small">Download on the</span>
                  <span className="badge-large">App Store</span>
                </div>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.personalnutri.app&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="store-button google-play">
              <div className="badge-content">
                <div className="play-icon">▶</div>
                <div className="badge-text">
                  <span className="badge-small">GET IT ON</span>
                  <span className="badge-large">Google Play</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div className="cookie-banner">
          <div className="cookie-content">
            <div className="cookie-icon">
              🍪
            </div>
            <div className="cookie-text">
              <h3 className="cookie-title">{t('cookies.title')}</h3>
              <p className="cookie-description">
                {t('cookies.description')}
              </p>
              <div className="cookie-purposes">
                <strong>{t('cookies.purposes')}</strong>
                <ul>
                  {t('cookies.purposesList').map((purpose, index) => (
                    <li key={index}>{purpose}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="cookie-actions">
              <button 
                className="cookie-learn-more"
                onClick={handleCookieLearnMore}
                aria-label="Saiba mais sobre nossa política de cookies"
              >
                {t('cookies.learnMore')} →
              </button>
              <button 
                className="cookie-accept"
                onClick={handleCookieAccept}
                aria-label="Aceitar cookies"
              >
                {t('cookies.accept')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndexPage;

